import PropTypes from 'prop-types';
import React from 'react';
import { bindAll } from '@bibliocommons/utils-react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {
  ugcCommentShape,
  catalogBibShape,
  ugcRatingShape,
  userIdentityShape,
  childrenShape
} from '@bibliocommons/bc-prop-types';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';
import TextUgcDisplayContainer from 'app/components/bibs/shared/ugc/TextUgcDisplay';
import { CommentEditorContainer } from 'app/components/bibs/shared/ugc/CommentEditor';
import DeleteCommentModalContainer from 'app/components/bibs/shared/ugc/DeleteCommentModal';
import CommentsActions from 'app/actions/CommentsActions';

export default class CommentDisplay extends React.PureComponent {
  optionsDropdownRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = { editorIsOpen: false };
    bindAll(this);
  }

  componentDidUpdate(prevProps, prevState) {
    // Set focus on the options dropdown when the editor is closed.
    if (!this.state.editorIsOpen && prevState.editorIsOpen && this.optionsDropdownRef.current) {
      setFocusWithinElement(this.optionsDropdownRef.current);
    }
  }

  toggleEditor(value) {
    this.setState({ editorIsOpen: value });
  }

  openDeletionModal() {
    const commentId = this.props.comment.get('id');
    this.props.modalActions.openModal(`delete-comment-${commentId}`);
  }

  renderEditor() {
    const { catalogBib, comment } = this.props;
    return (
      <CommentEditorContainer
        catalogBib={catalogBib}
        commentId={comment.get('id')}
        handleCancel={() => this.toggleEditor(false)}
        handleSuccess={() => this.toggleEditor(false)}
      />
    );
  }

  likeComment = () => {
    const { comment, likesActions } = this.props;
    const commentId = comment.get('id');

    likesActions.addLike({
      contentId: commentId,
      postSuccess: () => CommentsActions.getComment(commentId)
    });
  };

  unlikeComment = () => {
    const { comment, currentUserLikes, likesActions } = this.props;
    const commentId = comment.get('id');

    likesActions.removeLike({
      id: currentUserLikes.getIn([commentId, 'id']),
      contentId: commentId,
      postSuccess: () => CommentsActions.getComment(commentId)
    });
  };

  render() {
    if (this.state.editorIsOpen) return <div className="cp-comment-display">{this.renderEditor()}</div>;

    const {
      comment,
      displayStaffBadge,
      users,
      currentUserRating,
      catalogBib,
      currentUserLikes,
      children,
      handleDeleteSuccess
    } = this.props;
    const author = users.get(comment.get('userId').toString());
    const commentId = comment.get('id');
    // Get rating from entities/ugc/rating for the current user's comments,
    // and from entities/ugc/comments for everything else
    const rating =
      author && author.get('loggedIn') && currentUserRating ? currentUserRating.get('rating') : comment.get('rating');
    const likesCount = comment.get('likesCount');
    return (
      <div className="cp-comment-display">
        <TextUgcDisplayContainer
          ugcType="comment"
          catalogBib={catalogBib}
          author={author}
          id={commentId}
          key={commentId}
          createdDate={comment.get('createdDate')}
          rating={rating}
          likesCount={likesCount}
          isLikedByCurrentUser={!!currentUserLikes.get(commentId)}
          displayStaffBadge={displayStaffBadge}
          optionsDropdownRef={this.optionsDropdownRef}
          handleDelete={this.openDeletionModal}
          handleEdit={() => this.toggleEditor(true)}
          handleLike={this.likeComment}
          handleUnlike={this.unlikeComment}
        >
          {children}
        </TextUgcDisplayContainer>

        <DeleteCommentModalContainer
          commentId={commentId}
          catalogBib={catalogBib}
          handleSuccess={handleDeleteSuccess}
        />
      </div>
    );
  }
}

CommentDisplay.defaultProps = {
  displayStaffBadge: true
};

CommentDisplay.propTypes = {
  comment: ugcCommentShape.isRequired,
  users: ImmutablePropTypes.mapOf(userIdentityShape).isRequired,
  catalogBib: catalogBibShape.isRequired,
  currentUserRating: ugcRatingShape,
  displayStaffBadge: PropTypes.bool,
  handleDeleteSuccess: PropTypes.func,
  currentUserLikes: ImmutablePropTypes.mapOf(ImmutablePropTypes.map).isRequired,
  children: childrenShape.isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired,
  likesActions: PropTypes.objectOf(PropTypes.func).isRequired
};
